import React from "react";
import { Link } from "react-router-dom";
import { Gauge, Calculator, ArrowUpRight } from "lucide-react";
import useSEO from "@/lib/useSEO";
import JsonLd from "@/components/JsonLd";
import FAQAccordion from "@/components/FAQAccordion";
import { SITE_URL, breadcrumbSchema, itemListSchema, faqPageSchema } from "@/lib/schema";

const TOOLS_FAQ = [
  {
    q: "Who are these tools built for?",
    a: "The Typing Speed Test is for aspiring court reporters, CART providers, broadcast captioners, students and anyone curious about how their typing speed compares to certification standards. The ROI Calculator is for attorneys, paralegals and legal-ops teams evaluating AI legal drafting adoption.",
  },
  {
    q: "Do the tools work in every US city?",
    a: "Yes — the tools are cloud-based and work anywhere. They are especially relevant in the largest US legal markets: Washington DC, New York City, Los Angeles, Chicago, Houston and Atlanta — where LexWorkspace publishes dedicated city guides.",
  },
  {
    q: "Which platforms do these tools connect to?",
    a: "The Typing Speed Test funnels to StenoPaceMaster (stenopacemaster.com) for stenography training. The ROI Calculator funnels to DraftMaster.ai (draftmaster.ai) for AI legal drafting. Each tool also cross-promotes the other product where relevant.",
  },
];

const TOOLS = [
  {
    slug: "typing-speed-test",
    name: "Typing Speed Test",
    tagline: "How do you compare to a court reporter?",
    description:
      "WPM & accuracy test measured against real certification speeds — RPR 180/200/225, RMR 260, CRR realtime. See what steno hardware unlocks.",
    Icon: Gauge,
    color: "#1E3A8A",
    bg: "#EFF6FF",
    for: "For court reporters, captioners, students & anyone curious",
  },
  {
    slug: "drafting-roi-calculator",
    name: "AI Legal Drafting ROI Calculator",
    tagline: "Estimate your firm's annual savings",
    description:
      "Six inputs. Live results. See exactly how many hours and how much revenue an AI legal drafting platform would recover across your team.",
    Icon: Calculator,
    color: "#065F46",
    bg: "#ECFDF5",
    for: "For attorneys, paralegals, and legal operations",
  },
];

export default function ToolsIndex() {
  useSEO({
    title: "Interactive Tools — Typing Speed Test & AI Drafting ROI Calculator | LexWorkspace",
    description:
      "Interactive tools for court reporters and legal teams: measure your typing speed against court reporter certification benchmarks, and calculate your firm's AI legal drafting ROI.",
    keywords:
      "typing speed test, court reporter speed test, AI legal drafting ROI calculator, law firm ROI calculator, WPM test",
    url: SITE_URL + "/tools",
  });

  return (
    <div data-testid="tools-page">
      <JsonLd id="tools-faq" data={faqPageSchema(TOOLS_FAQ)} />
      <JsonLd
        id="tools-breadcrumbs"
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          { name: "Tools", url: SITE_URL + "/tools" },
        ])}
      />
      <JsonLd
        id="tools-itemlist"
        data={itemListSchema(
          TOOLS.map((t) => ({
            name: t.name,
            url: `${SITE_URL}/tools/${t.slug}`,
          })),
          "LexWorkspace Tools"
        )}
      />

      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
            Interactive tools
          </p>
          <h1
            data-testid="tools-h1"
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] max-w-4xl"
          >
            Interactive tools.
          </h1>
          <p className="text-gray-600 mt-6 max-w-2xl leading-relaxed">
            Two hands-on tools — one for the stenography side, one for the legal
            drafting side.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-0 border border-black/10">
          {TOOLS.map((t, i) => {
            const Icon = t.Icon;
            return (
              <Link
                key={t.slug}
                to={`/tools/${t.slug}`}
                data-testid={`tools-card-${t.slug}`}
                className={
                  "group p-10 lg:p-14 transition-colors " +
                  (i === 0 ? "md:border-r border-b md:border-b-0 border-black/10" : "")
                }
                style={{ "--hover-bg": t.bg }}
                onMouseEnter={(e) => (e.currentTarget.style.background = t.bg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
              >
                <Icon size={32} style={{ color: t.color }} strokeWidth={1.5} className="mb-8" />
                <p
                  className="text-xs tracking-[0.25em] uppercase font-semibold mb-3"
                  style={{ color: t.color }}
                >
                  {t.for}
                </p>
                <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-3">
                  {t.name}
                </h2>
                <p className="font-serif-display text-xl text-gray-700 mb-4">
                  {t.tagline}
                </p>
                <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                  {t.description}
                </p>
                <span
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold group-hover:gap-3 transition-all"
                  style={{ color: t.color }}
                >
                  Open tool <ArrowUpRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* GEO + FAQ */}
      <section className="border-t border-black/10 py-16 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Frequently asked
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-4">
              About LexWorkspace tools.
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Interactive tools serving professionals in Washington DC,
              New York City, Los Angeles, Chicago, Houston, Atlanta and across
              the US.
            </p>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={TOOLS_FAQ} testIdPrefix="tools-faq" />
          </div>
        </div>
      </section>
    </div>
  );
}
